import React from "react";
import { FaInfoCircle, FaRedo } from "react-icons/fa";

import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import ScrollIndicator from "../../Home/ScrollIndicator";
import useScrollToTop from "../../../hooks/useScollToTop";

import AdherentEventSelector from "./../EventPage/components/AdherentEventSelector";

import EditPalmaresModal from "./components/EditPalmaresModal";
import PalmaresEmptyState from "./components/PalmaresEmptyState";
import PalmaresHero from "./components/PalmaresHero";
import PalmaresImageModal from "./components/PalmaresImageModal";
import PalmaresPodiumCarousel from "./components/PalmaresPodiumCarousel";
import PalmaresSkeleton from "./components/PalmaresSkeleton";
import PalmaresStats from "./components/PalmaresStats";
import PalmaresTable from "./components/PalmaresTable";
import PalmaresToolbar from "./components/PalmaresToolbar";

import { usePalmaresPage } from "./hooks/usePalmaresPage";

function PalmaresPage() {
  useScrollToTop();

  const {
    adherents,
    selectedAdherentId,
    adherentsLoading,

    palmares,
    filteredPalmares,
    filters,
    selectedPalmares,
    selectedImage,
    isEditModalOpen,

    loading,
    refreshing,
    saving,
    deletingIds,

    activeFiltersCount,
    stats,
    years,

    fetchPalmares,
    selectAdherent,
    setSelectedImage,

    handleFilterChange,
    resetFilters,
    handleEdit,
    handleCloseEditModal,
    handleSave,
    handleDeleteWithConfirm,
    handleImageClick,
    handleOpenLink,
  } = usePalmaresPage();

  return (
    <main className="min-h-screen bg-slate-50 pb-24 pt-[88px] text-slate-950 dark:bg-slate-950 dark:text-white lg:pt-[104px]">
      <PalmaresHero stats={stats} />

      <section className="mx-auto -mt-12 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] border border-white/80 bg-white/95 p-4 shadow-2xl shadow-slate-200/70 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/95 dark:shadow-black/30 sm:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-amber-700 dark:bg-amber-500/10 dark:text-amber-200">
                <FaInfoCircle />
                Historique sportif
              </div>

              <h1 className="mt-4 font-titre text-3xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-4xl">
                Mon palmarès
              </h1>

              <p className="mt-3 max-w-3xl text-sm font-medium leading-7 text-slate-600 dark:text-white/60 sm:text-base">
                Consulte les résultats enregistrés dans le palmarès du judoka
                sélectionné : classement, combats, observations, podiums et
                tableaux associés.
              </p>
            </div>

            <button
              type="button"
              onClick={() => fetchPalmares()}
              disabled={refreshing}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:border-amber-200 hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:hover:border-amber-300/40 dark:hover:text-amber-100 lg:min-w-[220px]"
            >
              <FaRedo className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Actualisation..." : "Actualiser"}
            </button>
          </div>

          <div className="mt-5 rounded-[1.5rem] border border-amber-100 bg-amber-50/80 p-4 dark:border-amber-300/10 dark:bg-amber-500/10">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-amber-700 shadow-sm dark:bg-white/10 dark:text-amber-100">
                <FaInfoCircle />
              </div>

              <div>
                <p className="text-sm font-bold text-slate-950 dark:text-white">
                  Palmarès du judoka sélectionné
                </p>

                <p className="mt-1 text-sm font-medium leading-6 text-slate-600 dark:text-white/60">
                  Choisis le profil sportif à afficher. Les palmarès ajoutés
                  depuis la page Résultats apparaissent ici sous forme de
                  tableau, avec un carrousel compact pour les podiums en image.
                </p>
              </div>
            </div>
          </div>

          <PalmaresStats stats={stats} />
        </div>
      </section>

      <section className="mx-auto mt-6 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        {!adherentsLoading && adherents.length > 0 && (
          <AdherentEventSelector
            adherents={adherents}
            selectedId={selectedAdherentId}
            onSelect={selectAdherent}
          />
        )}

        <PalmaresToolbar
          filters={filters}
          years={years}
          onFilterChange={handleFilterChange}
          onReset={resetFilters}
          activeFiltersCount={activeFiltersCount}
          resultsCount={filteredPalmares.length}
          totalCount={palmares.length}
        />

        {loading ? (
          <PalmaresSkeleton />
        ) : filteredPalmares.length > 0 ? (
          <>
            <PalmaresPodiumCarousel
              palmares={filteredPalmares}
              onImageClick={handleImageClick}
              onOpenLink={handleOpenLink}
            />

            <PalmaresTable
              palmares={filteredPalmares}
              deletingIds={deletingIds}
              onEdit={handleEdit}
              onDelete={handleDeleteWithConfirm}
              onImageClick={handleImageClick}
              onOpenLink={handleOpenLink}
            />
          </>
        ) : (
          <PalmaresEmptyState onReset={resetFilters} />
        )}
      </section>

      <EditPalmaresModal
        isOpen={isEditModalOpen}
        palmares={selectedPalmares}
        saving={saving}
        onClose={handleCloseEditModal}
        onSave={handleSave}
      />

      <PalmaresImageModal
        imageUrl={selectedImage}
        onClose={() => setSelectedImage(null)}
      />

      <DashboardNavigation />
      <ScrollIndicator />
    </main>
  );
}

export default PalmaresPage;